import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchMC, FetchMCs, FetchMCsCounts } from 'src/constants/actionTypes'
import { mcPageSizeOptions, mcInitialSortFields, mcColumns } from 'src/constants/mc/mcGrid'
import { IMCsTableContext, MCsTableContext } from './MCsTable.context'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getSelectedMarket } from 'src/selectors/market'
import { MCsHeader } from '../grid/MCsHeader'
import { handleApiError } from 'src/utils/errorHandlers'
import { Pagination } from 'src/interfaces/table'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { MCsContext } from '../mcs/MCs.context'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { EmptyMCs } from '../grid/EmptyMCs'
import { MCStatus } from 'src/constants/mc/enums'
import { MC, MCDetails } from 'src/interfaces/mc'
import { MCDetailsModal } from 'src/components/mc-details/MCDetailsModal'

interface FetchMCsParams {
  limit?: number
  offset?: number
  searchQuery: string
  tagsFilter: Array<string>
  sortModel: GridSortModel
  selectedStatus: MCStatus
}

interface MCsTableProps {
  selectedStatus: MCStatus
}

const getRowId = (row: MC) => row.masterContractorId

const MCsTableComponent = ({ selectedStatus }: MCsTableProps) => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)

  const { searchQuery } = useContext(MCsContext)

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(mcPageSizeOptions)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [mcs, setMCs] = useState<Array<MC>>([])
  const [totalMCs, setTotalMCs] = useState<number>(0)
  const [tagsFilter, setTagsFilter] = useState<Array<string>>([])
  const [selectedMCId, setSelectedMCId] = useState<string | null>(null)
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mcDetails, setMCDetails] = useState<MCDetails | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>(
    mcInitialSortFields[selectedStatus]
      ? [
          {
            field: mcInitialSortFields[selectedStatus] || 'invitedAt',
            sort: 'desc',
          },
        ]
      : [],
  )

  const fetchMCs = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      sortModel,
      selectedStatus,
      tagsFilter,
    }: FetchMCsParams) => {
      setIsLoading(true)

      try {
        const params: any = {
          limit,
          offset,
          status: selectedStatus,
        }

        if (searchQuery) {
          params.search = searchQuery
        }

        if (tagsFilter?.length) {
          params.tags = tagsFilter
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0]?.field}:${sortModel[0]?.sort?.toUpperCase()}`
        }

        const payload: { data: Array<MC>; pagination: Pagination } = await createAsyncAction(
          dispatch,
          FetchMCs.request(params),
        )

        setMCs(payload.data)
        setTotalMCs(payload.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [selectedMarket?.id],
  )

  const fetchMCsCounts = useCallback(() => {
    const params: any = {}

    if (selectedMarket?.id) {
      params.partnerMarketId = selectedMarket?.id
    }

    dispatch(FetchMCsCounts.request(params))
  }, [selectedMarket?.id])

  const refreshMCs = useCallback(async () => {
    await fetchMCs({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      selectedStatus,
    })
    fetchMCsCounts()
  }, [limit, offset, searchQuery, sortModel, tagsFilter, selectedStatus, fetchMCs, fetchMCsCounts])

  useEffect(() => {
    fetchMCs({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      selectedStatus,
    })
  }, [limit, offset, searchQuery, sortModel, tagsFilter, selectedStatus, selectedMarket?.id])

  const handleRowClick = useCallback(({ row }: { row: MC }) => {
    setSelectedMCId(row.masterContractorId)
  }, [])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const mcsContextValue = useMemo<IMCsTableContext>(() => {
    return {
      tagsFilter,
      setTagsFilter,

      refreshMCs,
    }
  }, [tagsFilter, setTagsFilter, refreshMCs])

  const fetchMCDetails = useCallback(async (masterContractorId: string) => {
    try {
      const mc: MCDetails = await createAsyncAction(
        dispatch,
        FetchMC.request({ masterContractorId }),
      )
      setMCDetails(mc)
    } catch (err) {
      handleApiError(err)
    }
  }, [])

  useEffect(() => {
    if (!selectedMCId) {
      setMCDetails(null)
    } else {
      fetchMCDetails(selectedMCId)
    }
  }, [selectedMCId])

  const closeMCDetails = useCallback(() => {
    setMCDetails(null)
    setSelectedMCId(null)
  }, [])

  const refreshMCDetails = useCallback(async () => {
    if (selectedMCId) {
      await fetchMCDetails(selectedMCId)
    }
    refreshMCs()
  }, [selectedMCId, refreshMCs])

  const columns = useMemo(() => mcColumns[selectedStatus], [selectedStatus])

  return (
    <MCsTableContext.Provider value={mcsContextValue}>
      <MCsHeader />
      <GridTable<MC>
        rows={mcs}
        isLoading={isLoading}
        columns={columns}
        paginationModel={paginationModel}
        pageSizeOptions={mcPageSizeOptions}
        rowCount={totalMCs}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handleChangePagination}
        onRowClick={handleRowClick}
        getRowId={getRowId}
        EmptyElement={EmptyMCs}
      />
      {mcDetails && (
        <MCDetailsModal
          mcDetails={mcDetails}
          onClose={closeMCDetails}
          onRefresh={refreshMCDetails}
        />
      )}
    </MCsTableContext.Provider>
  )
}

export const MCsTable = memo(MCsTableComponent)
